import { useEffect } from 'react';
import { useQueryClient } from 'react-query';

import { isSessionError } from '../hooks/useSessionGuard';
import { track } from './client';
import { safely } from './safely';

const MAX_MESSAGE_LENGTH = 200;

// The first key element names the endpoint; the rest carry page numbers and typed filter text.
const keyOf = (key: unknown): string =>
  Array.isArray(key) ? String(key[0] ?? '') : String(key ?? '');

const messageOf = (error: unknown): string => {
  const message = (error as { message?: unknown } | null)?.message;
  const text =
    typeof message === 'string' && message ? message : String(error ?? '');
  return text.slice(0, MAX_MESSAGE_LENGTH);
};

// Sibling of useSessionGuard: the same cache subscriptions, reporting instead of redirecting.
// eslint-disable-next-line import/prefer-default-export
export function useRequestFailureTelemetry(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // A mutation notifies on every state change; report its failure once.
    const reported = new WeakSet<object>();

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) =>
      safely(() => {
        // 'error' is the settled failure; 'failed' fires per retry.
        if (event?.type !== 'queryUpdated' || event.action.type !== 'error')
          return;
        const { error } = event.action;
        track('request_failed', {
          kind: 'query',
          key: keyOf(event.query.queryKey),
          message: messageOf(error),
          isSessionError: isSessionError(error),
        });
      }),
    );

    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((mutation) =>
        safely(() => {
          if (!mutation || mutation.state.status !== 'error') return;
          if (reported.has(mutation)) return;
          reported.add(mutation);
          const { error } = mutation.state;
          track('request_failed', {
            kind: 'mutation',
            key: keyOf(mutation.options.mutationKey),
            message: messageOf(error),
            isSessionError: isSessionError(error),
          });
        }),
      );

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient]);
}
